import { useCallback, useEffect, useRef } from 'react'

/**
 * The start/end window of one run, for games that report a finished run but never say when it began.
 *
 * The games' PlayCanvas builds send only an end-of-run message, so the window is inferred: it opens
 * when the game becomes ready and reopens the moment a run is submitted, making each run's window
 * "since the last thing that happened". That includes menu time between runs, which is the same time
 * the page-view timer would have counted for the games page.
 *
 * It also reopens when the tab becomes visible again, so a game left open in a background tab does
 * not bill hours of idling to the next run. That undercounts a run spanning a tab switch, which is
 * the right direction to be wrong in for a stats figure.
 *
 * ⚠ It does NOT bound a tab left open in the FOREGROUND — `visibilitychange` never fires, so an
 * idle hour lands on the next run. The API drops any run over an hour for that reason; do not treat
 * what this returns as trustworthy on its own.
 */
export default function useGameRunTimer(isReady: boolean): () => { startTime?: string; endTime?: string } {
  const startRef = useRef<Date | null>(null)

  useEffect(() => {
    if (isReady) startRef.current = new Date()
  }, [isReady])

  useEffect(() => {
    // Gated on `isReady` so a tab switch before the game reports ready cannot open a window for a
    // run that never started.
    if (!isReady) return

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') startRef.current = new Date()
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [isReady])

  /** Closes the current run's window and opens the next one. Empty until the game is ready. */
  return useCallback(() => {
    const start = startRef.current
    const end = new Date()
    startRef.current = end

    if (!start) return {}

    return { startTime: start.toISOString(), endTime: end.toISOString() }
  }, [])
}
